"use client";

import type { CSSProperties, KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib";
import { MorphEngine, type MorphEngineOptions } from "./morph-engine";
import { MORPH_DEFAULTS, type MorphTransition } from "./morph-shaders";
import { SafeWebGLBoundary } from "./safe-webgl-boundary";

export type { MorphTransition };

export interface MorphItem {
	image: string;
	caption?: string;
}

export interface MorphSliderProps {
	items?: MorphItem[];
	startIndex?: number;
	transition?: MorphTransition;
	duration?: number;
	ease?: string;
	intensity?: number;
	scale?: number;
	aberration?: number;
	drift?: number;
	autoplay?: boolean;
	autoplayDelay?: number;
	loop?: boolean;
	radius?: number;
	overlayColor?: string;
	showCaptions?: boolean;
	showControls?: boolean;
	showIndicators?: boolean;
	className?: string;
	[key: string]: unknown;
}

const DEFAULT_ITEMS: MorphItem[] = [
	{
		image: "https://images.unsplash.com/photo-1782977389500-dd7adad33ebe?q=80&w=1600&auto=format&fit=crop",
		caption: "One",
	},
	{
		image: "https://images.unsplash.com/photo-1781499455083-6ccc3beb20cd?q=80&w=1600&auto=format&fit=crop",
		caption: "Two",
	},
	{
		image: "https://images.unsplash.com/photo-1776394254711-4a0d7345269a?q=80&w=1600&auto=format&fit=crop",
		caption: "Three",
	},
	{
		image: "https://images.unsplash.com/photo-1781242629922-6f39cc3671cd?q=80&w=1600&auto=format&fit=crop",
		caption: "Four",
	},
];

export default function MorphSlider({
	items = DEFAULT_ITEMS,
	startIndex = 0,
	transition = MORPH_DEFAULTS.transition,
	duration = MORPH_DEFAULTS.duration,
	ease = MORPH_DEFAULTS.ease,
	intensity = MORPH_DEFAULTS.intensity,
	scale = MORPH_DEFAULTS.scale,
	aberration = MORPH_DEFAULTS.aberration,
	drift = MORPH_DEFAULTS.drift,
	autoplay = false,
	autoplayDelay = 4,
	loop = true,
	radius = 16,
	overlayColor = MORPH_DEFAULTS.overlayColor,
	showCaptions = true,
	showControls = true,
	showIndicators = true,
	className = "",
	...props
}: MorphSliderProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const engineRef = useRef<MorphEngine | null>(null);
	const [index, setIndex] = useState(startIndex);
	const [hovering, setHovering] = useState(false);

	const optsRef = useRef<MorphEngineOptions>({
		transition,
		duration,
		ease,
		intensity,
		scale,
		aberration,
		drift,
		overlayColor,
		loop,
	});
	optsRef.current = {
		transition,
		duration,
		ease,
		intensity,
		scale,
		aberration,
		drift,
		overlayColor,
		loop,
	};

	useEffect(() => {
		if (!containerRef.current) {
			return undefined;
		}
		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		const engine = MorphEngine.create(containerRef.current, {
			items,
			startIndex,
			reducedMotion,
			dprCap: 2,
			getOptions: () => optsRef.current,
			onIndexChange: setIndex,
		});
		engineRef.current = engine;
		setIndex(startIndex);

		return () => {
			engine?.destroy();
			engineRef.current = null;
		};
	}, [items, startIndex]);

	const handleNext = useCallback(() => engineRef.current?.next(), []);
	const handlePrev = useCallback(() => engineRef.current?.prev(), []);

	useEffect(() => {
		if (!autoplay || hovering) {
			return undefined;
		}
		const id = window.setTimeout(
			() => engineRef.current?.next(),
			Math.max(autoplayDelay, 1) * 1000,
		);
		return () => window.clearTimeout(id);
	}, [autoplay, autoplayDelay, hovering, index]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return undefined;
		}
		let startX = 0;
		let width = 1;
		let active = false;

		const onDown = (e: PointerEvent) => {
			const rect = el.getBoundingClientRect();
			width = rect.width || 1;
			startX = e.clientX;
			const px = (e.clientX - rect.left) / rect.width;
			const py = (e.clientY - rect.top) / rect.height;
			engineRef.current?.setPointer(px, 1 - py);
			active = engineRef.current?.beginDrag() ?? false;
			if (active && el.setPointerCapture) {
				try {
					el.setPointerCapture(e.pointerId);
				} catch {
					/* ignore */
				}
			}
		};
		const onMove = (e: PointerEvent) => {
			if (!active) {
				return;
			}
			const ndx = (e.clientX - startX) / width;
			engineRef.current?.drag(ndx);
		};
		const onUp = () => {
			if (!active) {
				return;
			}
			active = false;
			engineRef.current?.endDrag();
		};

		el.addEventListener("pointerdown", onDown);
		el.addEventListener("pointermove", onMove);
		el.addEventListener("pointerup", onUp);
		el.addEventListener("pointercancel", onUp);

		return () => {
			el.removeEventListener("pointerdown", onDown);
			el.removeEventListener("pointermove", onMove);
			el.removeEventListener("pointerup", onUp);
			el.removeEventListener("pointercancel", onUp);
		};
	}, []);

	const onKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === "ArrowRight") {
				e.preventDefault();
				handleNext();
			} else if (e.key === "ArrowLeft") {
				e.preventDefault();
				handlePrev();
			}
		},
		[handleNext, handlePrev],
	);

	const hasCaptions = items.some((item) => item.caption);

	return (
		<div
			className={cn(
				"relative h-full w-full select-none overflow-hidden bg-[#0c0c0e]",
				className,
			)}
			style={
				{
					borderRadius: `${radius}px`,
					"--ms-swap": `${(duration * 0.66).toFixed(3)}s`,
					"--ms-dot": `${(duration * 0.45).toFixed(3)}s`,
					touchAction: "pan-y",
				} as CSSProperties
			}
			onMouseEnter={() => setHovering(true)}
			onMouseLeave={() => setHovering(false)}
			{...props}
		>
			<SafeWebGLBoundary>
				<div
					ref={containerRef}
					className="absolute inset-0 cursor-grab outline-none focus-visible:shadow-[inset_0_0_0_2px_rgba(255,255,255,0.7)] active:cursor-grabbing"
					role="group"
					aria-roledescription="carousel"
					aria-label="Image morph slider"
					tabIndex={0}
					onKeyDown={onKeyDown}
				/>
			</SafeWebGLBoundary>

			{showCaptions && hasCaptions && (
				<div
					className="morph-slider-caption pointer-events-none absolute bottom-[22px] left-[22px] z-[2] grid max-w-[70%]"
					aria-live="polite"
				>
					{items.map((item, i) =>
						item.caption ? (
							<span
								key={item.image + String(i)}
								aria-hidden={i === index ? undefined : true}
								className={cn(
									"morph-slider-caption-text pointer-events-none inline-block rounded-[10px] bg-[rgba(10,10,12,0.42)] px-[14px] py-[8px] text-[15px] font-semibold tracking-[0.01em] text-white backdrop-blur-[8px] [grid-area:1/1] [justify-self:start] [transition:opacity_var(--ms-swap)_cubic-bezier(0.16,1,0.3,1),transform_var(--ms-swap)_cubic-bezier(0.16,1,0.3,1),filter_var(--ms-swap)_cubic-bezier(0.16,1,0.3,1)]",
									i === index
										? "opacity-100 [transform:translateY(0)] [filter:blur(0)]"
										: "opacity-0 [transform:translateY(12px)] [filter:blur(6px)]",
								)}
							>
								{item.caption}
							</span>
						) : null,
					)}
				</div>
			)}

			{showControls && (
				<div className="pointer-events-none absolute top-1/2 right-0 left-0 z-[3] flex -translate-y-1/2 justify-between px-4">
					<button
						type="button"
						className="pointer-events-auto inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-[rgba(12,12,14,0.4)] text-white backdrop-blur-md transition-transform duration-200 hover:scale-105 hover:bg-[rgba(24,24,28,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80 active:scale-95"
						aria-label="Previous slide"
						onClick={handlePrev}
					>
						<svg
							viewBox="0 0 24 24"
							width="18"
							height="18"
							aria-hidden="true"
						>
							<path
								d="M15 5l-7 7 7 7"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						type="button"
						className="pointer-events-auto inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-[rgba(12,12,14,0.4)] text-white backdrop-blur-md transition-transform duration-200 hover:scale-105 hover:bg-[rgba(24,24,28,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80 active:scale-95"
						aria-label="Next slide"
						onClick={handleNext}
					>
						<svg
							viewBox="0 0 24 24"
							width="18"
							height="18"
							aria-hidden="true"
						>
							<path
								d="M9 5l7 7-7 7"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</div>
			)}

			{showIndicators && (
				<div
					className="absolute right-0 bottom-[18px] left-0 z-[3] flex items-center justify-center gap-2"
					role="tablist"
					aria-label="Slides"
				>
					{items.map((item, i) => (
						<button
							key={item.image + String(i)}
							type="button"
							role="tab"
							aria-selected={i === index}
							aria-label={`Go to slide ${i + 1}`}
							className={cn(
								"h-2 cursor-pointer rounded-full [transition:width_var(--ms-dot)_cubic-bezier(0.16,1,0.3,1),background-color_var(--ms-dot)_ease] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80",
								i === index
									? "w-[22px] bg-white/95"
									: "w-2 bg-white/35",
							)}
							onClick={() => {
								const engine = engineRef.current;
								if (!engine || i === index) {
									return;
								}
								engine.goToIndex(i);
							}}
						/>
					))}
				</div>
			)}
		</div>
	);
}
